import type { DrawerProps } from '@mui/material';
import { Divider, Drawer, IconButton } from '@mui/material';
import { isFixedDeductionProductOnly, isPercentageDeductionProductOnly } from '@vy/services';
import type { FixedProductInput } from '@vy/types';
import { DEFAULT_CURRENCY } from '@vy/types';
import { raise, vySerialize, withDefault } from '@vy/utils';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CloseIcon } from '../../../../../components/icons';
import { ChargeProvider } from '../charge-context';
import type { NewPlanForm } from '../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { CreditChargeEditDrawerContent } from './promotion-charge-edit-drawer-components/contents/CreditChargeEditDrawerContent';
import { PercentageChargeEditDrawerContent } from './promotion-charge-edit-drawer-components/contents/PercentageChargeEditDrawerContent';

type PromotionChargeEditDrawerProps = Pick<DrawerProps, 'open' | 'onClose'> & {
  promotion: FixedProductInput;
};

export const PromotionChargeEditDrawer: React.FC<PromotionChargeEditDrawerProps> = ({
  promotion,
  ...props
}) => {
  const { getValues } = useFormContext<PlanInputForm, unknown, NewPlanForm>();
  const promotions = withDefault(getValues('promotionProducts'), []);
  const promotionIndex = promotions.findIndex(({ id }) => id === promotion.id);

  const ChargeEditDrawerItem = useMemo(() => chargeEditDrawerItemFactory().create(promotion), [promotion]);

  const handleCloseClick = () => {
    props.onClose?.({}, 'backdropClick');
  };

  return (
    <Drawer
      anchor="right"
      slotProps={{
        backdrop: {
          invisible: true,
        },

        paper: {
          className: 'bg-transparent',
          elevation: 0,
        }
      }}
      {...props}>
      <div className="m-4 h-full min-w-[380px] max-w-[380px] rounded-lg border border-[#E7EEF3] bg-white shadow-[0px_8px_24px_0px_#00000014]">
        <div className="flex items-baseline justify-between p-4">
          <span>{promotion.name}</span>
          <IconButton onClick={handleCloseClick}>
            <CloseIcon color="#576063" width={20} height={20} />
          </IconButton>
        </div>

        <Divider />

        <div className="p-6">
          <ChargeProvider
            value={{
              item: promotion,
              itemIndex: promotionIndex,
              editable: true,
              currency: DEFAULT_CURRENCY,
            }}
          >
            <ChargeEditDrawerItem />
          </ChargeProvider>
        </div>
      </div>
    </Drawer>
  );
};

function chargeEditDrawerItemFactory(): {
  create: (promotion: FixedProductInput) => React.FC;
} {
  const create = (promotion: FixedProductInput): React.FC => {
    if (isFixedDeductionProductOnly(promotion)) {
      return CreditChargeEditDrawerContent;
    }

    if (isPercentageDeductionProductOnly(promotion)) {
      return PercentageChargeEditDrawerContent;
    }

    return raise(`unknown product type ${vySerialize(promotion, null, 2)}`);
  };

  return {
    create,
  };
}
